/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError } from 'axios'
import {
  DuplicatePracticeTestBody,
  PracTestListParticipantsParams,
  PracTestListParticipantsRes,
  PracTestOverviewRes,
  PracticeTest,
  PracticeTestBody,
  PracticeTestDetail,
  PracticeTestDetailParams,
  PracticeTestListParams,
  PracticeTestListResponse,
  UpdatePracticeTestBody,
  UpdatePracticeTestResponse,
} from '@/types/practice-test'
import BaseService from './baseService'

class PracticeTestService extends BaseService {
  async createPracticeTest(body: PracticeTestBody): Promise<PracticeTest> {
    try {
      const response = await this.post<PracticeTest>(
        '/practice-test/create-practice-test',
        body
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to create practice test'
        )
      }
      throw error
    }
  }

  async getPracticeTest(
    params: PracticeTestDetailParams
  ): Promise<PracticeTestDetail> {
    try {
      const response = await this.get<PracticeTestDetail>(
        `/practice-test/${params.practice_test_id}`,
        params
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to fetch practice test'
        )
      }
      throw error
    }
  }

  async publishPracticeTest(practice_test_id: string): Promise<PracticeTest> {
    try {
      const response = await this.patch<PracticeTest>(
        `/practice-test/${practice_test_id}/publish`
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to publish practice test'
        )
      }
      throw error
    }
  }

  async unpublishPracticeTest(practice_test_id: string): Promise<PracticeTest> {
    try {
      const response = await this.patch<PracticeTest>(
        `/practice-test/${practice_test_id}/unpublish`
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to publish practice test'
        )
      }
      throw error
    }
  }

  async getPracticeTestList(
    params: PracticeTestListParams
  ): Promise<PracticeTestListResponse> {
    try {
      const queryParams: Record<string, unknown> = {
        page: params.page,
        limit: params.limit,
        search: params.search,
        is_practice: params.is_practice,
      }

      if (params.status != null) {
        queryParams.status = params.status
      }

      if (params.creator_type) {
        queryParams.creator_type = params.creator_type
      }

      const response = await this.get<PracticeTestListResponse>(
        `/class/${params.class_id}/practice-tests`,
        queryParams
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to fetch practice test list'
        )
      }
      throw error
    }
  }

  async duplicatePracticeTest(
    body: DuplicatePracticeTestBody
  ): Promise<PracticeTest[]> {
    try {
      const response = await this.post<PracticeTest[]>(
        '/practice-test/duplicate',
        body
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to duplicate practice test'
        )
      }
      throw error
    }
  }

  async deleteDraftPracticeTest(practice_test_id: string): Promise<any> {
    try {
      const response = await this.delete<any>(
        `/practice-test/${practice_test_id}`
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to delete draft practice test'
        )
      }
      throw error
    }
  }

  async updatePracticeTest(
    practice_test_id: string,
    body: UpdatePracticeTestBody
  ): Promise<UpdatePracticeTestResponse> {
    try {
      const response = await this.patch<UpdatePracticeTestResponse>(
        `/practice-test/update/${practice_test_id}`,
        body
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to update practice test'
        )
      }
      throw error
    }
  }

  async getPracTestOverview(
    practice_test_id: number
  ): Promise<PracTestOverviewRes> {
    try {
      const response = await this.get<PracTestOverviewRes>(
        `/practice-test/${practice_test_id}/overview`
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message ||
            'Failed to fetch practice test overview'
        )
      }
      throw error
    }
  }

  async getPracTestListParticipants(
    params: PracTestListParticipantsParams
  ): Promise<PracTestListParticipantsRes> {
    try {
      // Filter out null/undefined parameters
      const queryParams = new URLSearchParams()

      if (params.page != null)
        queryParams.append('page', params.page.toString())
      if (params.limit != null)
        queryParams.append('limit', params.limit.toString())
      if (params.class_id != null && params.class_id !== 'all')
        queryParams.append('class_id', params.class_id.toString())
      if (params.search != null && params.search !== '')
        queryParams.append('search', params.search)
      if (params.result !== null && params.result !== undefined)
        queryParams.append('result', params.result)

      const queryString = queryParams.toString()
      const url = `/practice-test/${params.practice_test_id}/participants${queryString ? `?${queryString}` : ''}`

      const response = await this.get<PracTestListParticipantsRes>(url)
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message ||
            'Failed to fetch practice test list participants'
        )
      }
      throw error
    }
  }
}

export const practiceTestService = new PracticeTestService()
